'use client';

/**
 * GoogleReviewBadgePreview — animated reproduction of the live Google review
 * badge that ships on every SUM loyalty card.
 *
 * Mirrors the behavior of the real component
 * (sum/front-end/src/components/atoms/LoyaltyCardDefaultDisplay.tsx):
 * stars fill sequentially, the final star pings + glows when the row is
 * complete, then the cycle resets after a short pause. Purely visual
 * (no navigation, no props), used as a "context strip" preview on the
 * /google-reviews landing page.
 */

import React, { useEffect, useState } from 'react';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { HiOutlineStar, HiStar } from 'react-icons/hi2';

const STAR_COUNT = 5;
const STATIC_FILLED_STARS = 2;
const STAR_ANIMATION_INTERVAL = 600;
const HOLD_MS = 1400;

const STAR_INDEXES = Array.from({ length: STAR_COUNT }, (_, i) => i);

const GOOGLE_BRAND_LETTERS: { char: string; color: string }[] = [
  { char: 'G', color: '#4285F4' },
  { char: 'o', color: '#EA4335' },
  { char: 'o', color: '#FBBC05' },
  { char: 'g', color: '#4285F4' },
  { char: 'l', color: '#34A853' },
  { char: 'e', color: '#EA4335' },
];

type Props = { ariaLabel: string };

const GoogleReviewBadgePreview = ({ ariaLabel }: Props) => {
  const [activeCount, setActiveCount] = useState(STATIC_FILLED_STARS);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) {
      const t = setTimeout(() => {
        setActiveCount(STATIC_FILLED_STARS);
        setPaused(false);
      }, HOLD_MS);
      return () => clearTimeout(t);
    }
    if (activeCount >= STAR_COUNT) {
      setPaused(true);
      return;
    }
    const t = setTimeout(() => setActiveCount((n) => n + 1), STAR_ANIMATION_INTERVAL);
    return () => clearTimeout(t);
  }, [activeCount, paused]);

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-full border border-white/40 bg-white/95 px-2 py-1 text-gray-900 shadow-lg backdrop-blur cursor-default select-none"
    >
      <div className="relative flex flex-col items-center">
        <div className="relative flex items-center">
          {STAR_INDEXES.map((index) => {
            const isStaticFilled = index < STATIC_FILLED_STARS;
            const isActive = activeCount > index || isStaticFilled;
            const isCurrent = !paused && activeCount - 1 === index;
            const isFinalActive = isActive && index === STAR_COUNT - 1;

            return (
              <span
                key={index}
                className="relative flex items-center justify-center h-5 w-5 transition-transform duration-300"
                aria-hidden="true"
              >
                {isFinalActive && (
                  <>
                    <span className="absolute inset-[-6px] rounded-full border border-amber-400/80 opacity-70 animate-ping" />
                    <span className="absolute inset-0 rounded-full bg-amber-300/50 blur-sm" />
                  </>
                )}
                <span className={`transition-transform duration-300 ${isCurrent ? 'scale-110' : 'scale-100'}`}>
                  {isActive ? (
                    <HiStar
                      className={`text-amber-500 drop-shadow ${isFinalActive ? 'h-[18px] w-[18px]' : 'h-4 w-4'}`}
                    />
                  ) : (
                    <HiOutlineStar className="h-4 w-4 text-amber-300" />
                  )}
                </span>
              </span>
            );
          })}
        </div>
        <span className="flex items-center justify-center text-sm leading-tight">
          <span className="inline-flex items-center gap-[1px] font-bold">
            {GOOGLE_BRAND_LETTERS.map(({ char, color }, index) => (
              <span key={index} style={{ color }} className="leading-none">
                {char}
              </span>
            ))}
          </span>
        </span>
      </div>
      <div className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-amber-200 text-amber-900 shadow-inner">
        <HiOutlineArrowLeft className="h-4 w-4 rotate-180" aria-hidden="true" />
      </div>
    </div>
  );
};

export default GoogleReviewBadgePreview;
